import { useState, useRef } from "react";
import { NAVY } from "../styles/palette";
import { detectFileType, joinImport } from "../lib/importJoin";
import { parityFromDate } from "../lib/date";
import { useIsMobile } from "../lib/useIsMobile";

// Multi-file client importer. Operators drop their Client List + Subscriptions
// exports (in either order, together or one at a time); we detect each file, join
// them by street address, and decode the "Regular Plan - 2d-1xW" plan strings into
// dog count + cadence — the same join a human would do. A single pre-combined CSV
// (with Dogs/Frequency columns) also works: the join simply has nothing to add.
const TYPE_LABEL = { contacts: "Client List", subscriptions: "Subscriptions", unknown: "⚠ Unrecognized" };

export function BulkImport({ onImport, showToast }) {
  const [files,    setFiles]    = useState([]);   // [{ name, type, text }]
  const [result,   setResult]   = useState(null); // { clients, stats }
  const [loading,  setLoading]  = useState(false);
  const [imported, setImported] = useState(0);
  const fileRef = useRef(null);
  const isMobile = useIsMobile();

  async function handleFiles(e) {
    const chosen = Array.from(e.target.files || []);
    if (!chosen.length) return;
    try {
      const read = await Promise.all(chosen.map(async (f) => ({ name: f.name, type: detectFileType(await f.text()), text: await f.text() })));
      // merge with anything already loaded (so a 2nd pick adds the other file); same name replaces
      const merged = [...files.filter((x) => !read.some((r) => r.name === x.name)), ...read];
      setFiles(merged);
      setResult(joinImport(merged));
      setImported(0);
    } catch { showToast("Could not read the file(s)", "error"); }
  }

  function reset() {
    setFiles([]); setResult(null); setImported(0);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function handleImport() {
    if (!result?.clients.length) return;
    setLoading(true);
    await onImport(result.clients);
    setImported(result.clients.length);
    reset();
    setLoading(false);
  }

  // A known-good, ready-to-fill CSV — for operators whose old system exports
  // something odd, or who'd rather just list clients and set plans by hand later
  // (leave Frequency/Dogs blank and fill them in on each profile).
  function downloadTemplate() {
    const headers = ["First Name", "Last Name", "Email", "Phone", "Address", "City", "State", "Zip", "Frequency", "Dogs", "Deodorizer Frequency", "Last Service Date", "Last Deodorizer Date", "SMS Consent"];
    const rows = [
      ["Jane", "Doe", "jane@example.com", "215-555-0100", "123 Main St", "Philadelphia", "PA", "19119", "weekly", "2", "monthly", "2026-08-01", "2026-07-25", "Yes"],
      ["Mary", "Nguyen", "mary@example.com", "215-555-0188", "789 Elm Rd", "Aston", "PA", "19014", "biweekly", "1", "", "07/28/2026", "", "Yes"],
      ["John", "Smith", "john@example.com", "215-555-0142", "456 Oak Ave", "Ardmore", "PA", "19003", "", "", "", "", "", "No"],
    ];
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "scoopcrm-client-import-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    // Revoke LATER: iOS Safari hands the download to the share sheet / Files asynchronously,
    // and a synchronous revoke can abort it.
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }

  const st = result?.stats;
  // Bi-weekly rows with no Last Service Date. Their fortnight can't be derived, so
  // they'd import on a coin-flip week. Worth flagging HERE, while the operator still
  // has the export open and can add one column — not weeks later on the board.
  const noFortnight = (result?.clients || []).filter(
    (c) => (c.frequency === "biweekly" || c.deoFreq === "biweekly") && !c.lastServiceDate);
  // Monthly deo has FOUR possible phases (which week of the month), so a guess is wrong
  // three times out of four — the worst odds of any cadence we schedule.
  const noDeoAnchor = (result?.clients || []).filter((c) => c.deoFreq === "monthly" && !c.lastDeoDate);
  const hasSubs = files.some((f) => f.type === "subscriptions");
  const hasContacts = files.some((f) => f.type === "contacts");

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <input ref={fileRef} type="file" accept=".csv" multiple onChange={handleFiles}
          style={{ flex: 1, minWidth: 240, padding: "8px 12px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 13, fontFamily: "Inter,sans-serif" }} />
        <button className="btn btn-ghost btn-sm" type="button" onClick={downloadTemplate}>⬇ Template CSV</button>
        {result?.clients.length > 0 && (
          <button className="btn btn-gold" onClick={handleImport} disabled={loading}>
            {loading ? "Importing…" : `Import ${result.clients.length} Clients`}
          </button>
        )}
        {files.length > 0 && <button className="btn btn-ghost btn-sm" onClick={reset} disabled={loading}>Clear</button>}
      </div>

      {/* detected files */}
      {files.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {files.map((f) => (
            // Phone: no hover, so the badge wraps and shows the full file name instead of a title tooltip.
            <span key={f.name} className={`badge ${f.type === "unknown" ? "badge-gold" : "badge-blue"}`} title={f.name}
              style={isMobile ? { whiteSpace: "normal", maxWidth: "100%", overflowWrap: "anywhere", lineHeight: 1.35, padding: "5px 12px" } : undefined}>
              {f.type === "unknown" ? "⚠" : "✓"} {TYPE_LABEL[f.type]} · {!isMobile && f.name.length > 22 ? f.name.slice(0, 20) + "…" : f.name}
            </span>
          ))}
        </div>
      )}

      {imported > 0 && (
        <div style={{ background: "#e3fcef", border: "1px solid #abf5d1", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#006644", marginBottom: 12 }}>
          ✅ Imported {imported} client{imported === 1 ? "" : "s"} (Payment Pending). Assign technicians and collect payment from each profile.
        </div>
      )}

      {result?.clients.length > 0 && (
        <div>
          {/* join summary + gentle guidance */}
          <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 8, lineHeight: 1.6 }}>
            <strong>{st.contacts}</strong> client{st.contacts === 1 ? "" : "s"} ready
            {hasSubs && <> · <strong>{st.matched}</strong> matched to a plan</>}
            {st.noPlan > 0 && <> · <span style={{ color: "var(--danger)" }}><strong>{st.noPlan}</strong> missing dog count / cadence</span></>}
            {hasContacts && !hasSubs && <><br />💡 Also drop your <strong>Subscriptions</strong> export to fill in each client's dog count and cadence automatically.</>}
            {st.orphanSubs > 0 && <><br />ℹ️ {st.orphanSubs} plan{st.orphanSubs === 1 ? "" : "s"} had no matching client (skipped — add those people to your Client List).</>}
            {st.noPlan > 0 && <><br />⚠️ Clients missing a plan still import, but won't generate jobs until you set their frequency/dogs.</>}
            {noFortnight.length > 0 && (
              <><br />⚠️ <strong>{noFortnight.length} bi-weekly client{noFortnight.length === 1 ? "" : "s"} have no <em>Last Service Date</em></strong> — we can't tell which of the two alternating weeks they're on, so they'd land on a 50/50 guess and stay a week out of phase. Add a <strong>Last Service Date</strong> column (their most recent visit in your old system) and re-drop the file.</>
            )}
            {noDeoAnchor.length > 0 && (
              <><br />⚠️ <strong>{noDeoAnchor.length} monthly-deodorizer client{noDeoAnchor.length === 1 ? "" : "s"} have no <em>Last Deodorizer Date</em></strong> — a monthly treatment can fall in any of four weeks, so we'd have to pick one. They'll import on our default and self-correct after their first treatment, but adding the column gets it right from day one.</>
            )}
          </div>

          {isMobile ? (
            // Phone: the 10-column preview (~700px) would be a two-axis nested scroller with the
            // warning columns (Fortnight / SMS / Status) off-screen. Stack each row as a card and
            // let the page scroll — no fixed-height inner scroller.
            <div style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
              {result.clients.map((c, i) => {
                const badEmail = !(c.email && c.email.split("@").length === 2 && !/@email\.com$/i.test(c.email));
                const biweekly = c.frequency === "biweekly" || c.deoFreq === "biweekly";
                const fortnight = biweekly ? (c.lastServiceDate ? parityFromDate(c.lastServiceDate) : null) : "";
                return (
                  <div key={i} style={{ padding: "10px 12px", borderBottom: i < result.clients.length - 1 ? "1px solid var(--border)" : "none", background: i % 2 === 0 ? "#fff" : "#fafbfc", minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minWidth: 0 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                      {c._hasPlan
                        ? <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 99, background: "rgba(26,138,82,.14)", color: "var(--ok)" }}>✓ complete</span>
                        : <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 99, background: "#FEF6E7", color: "#8A5A06" }}>⚠ no plan</span>}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 3, color: badEmail ? "var(--danger)" : "var(--text2)", overflowWrap: "anywhere" }}>
                      {c.email || "⚠ missing email"}{c.phone ? <span style={{ color: "var(--text2)" }}> · {c.phone}</span> : null}{c.zip ? <span style={{ color: "var(--text2)" }}> · {c.zip}</span> : null}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 3, color: "var(--text2)", display: "flex", flexWrap: "wrap", gap: "2px 6px" }}>
                      <span style={{ color: c.frequency ? "var(--text2)" : "var(--danger)" }}>{c.frequency || "⚠ no frequency"}</span>
                      <span>·</span>
                      <span style={{ color: c.dogs != null ? "var(--text2)" : "var(--danger)" }}>{c.dogs != null ? `${c.dogs} dog${c.dogs === 1 ? "" : "s"}` : "⚠ no dog count"}</span>
                      {c.deoFreq && <><span>·</span><span>deo {c.deoFreq}</span></>}
                      {biweekly && (fortnight
                        ? <><span>·</span><span style={{ color: "#1a7f4b", fontWeight: 600 }}>{fortnight} weeks</span></>
                        : <><span>·</span><span style={{ color: "#974f0c", fontWeight: 600 }}>⚠ fortnight unknown — add a Last Service Date</span></>)}
                      {c.smsConsent && <><span>·</span><span>SMS ✓</span></>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
          <div style={{ maxHeight: 300, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 6 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ position: "sticky", top: 0, background: NAVY }}>
                <tr>
                  {["Name", "Email", "Phone", "Zip", "Freq", "Dogs", "Deo", "Fortnight", "SMS", "Status"].map((h) => (
                    <th key={h} style={{ padding: "7px 10px", textAlign: "left", fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.clients.map((c, i) => {
                  const badEmail = !(c.email && c.email.split("@").length === 2 && !/@email\.com$/i.test(c.email));
                  return (
                    <tr key={i} style={{ borderBottom: "1px solid var(--border)", background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                      <td style={{ padding: "7px 10px", fontSize: 12, fontWeight: 600 }}>{c.name}</td>
                      <td style={{ padding: "7px 10px", fontSize: 11, color: badEmail ? "var(--danger)" : "var(--text2)" }}>{c.email || "⚠ missing"}</td>
                      <td style={{ padding: "7px 10px", fontSize: 11, color: "var(--text2)" }}>{c.phone || "—"}</td>
                      <td style={{ padding: "7px 10px", fontSize: 11, color: "var(--text2)" }}>{c.zip || "—"}</td>
                      <td style={{ padding: "7px 10px", fontSize: 11, color: c.frequency ? "var(--text2)" : "var(--danger)" }}>{c.frequency || "—"}</td>
                      <td style={{ padding: "7px 10px", fontSize: 11, color: c.dogs != null ? "var(--text2)" : "var(--danger)" }}>{c.dogs != null ? c.dogs : "—"}</td>
                      <td style={{ padding: "7px 10px", fontSize: 11, color: "var(--text2)" }}>{c.deoFreq || "—"}</td>
                      <td style={{ padding: "7px 10px", fontSize: 11 }}>
                        {(c.frequency === "biweekly" || c.deoFreq === "biweekly")
                          ? (c.lastServiceDate
                              ? <span style={{ color: "#1a7f4b", fontWeight: 600 }}>{parityFromDate(c.lastServiceDate)}</span>
                              : <span style={{ color: "#974f0c", fontWeight: 600 }} title="No Last Service Date — the alternating week can't be determined">⚠ unknown</span>)
                          : <span style={{ color: "var(--text2)" }}>—</span>}
                      </td>
                      <td style={{ padding: "7px 10px", fontSize: 11, color: "var(--text2)" }}>{c.smsConsent ? "✓" : "—"}</td>
                      <td style={{ padding: "7px 10px", fontSize: 11 }}>
                        {c._hasPlan
                          ? <span style={{ color: "#1a7f4b", fontWeight: 600 }}>✓ complete</span>
                          : <span style={{ color: "#974f0c", fontWeight: 600 }}>⚠ no plan</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}
    </div>
  );
}
